import { useState, useEffect, useCallback, useMemo } from 'react';
// @mui
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import EmptyContent from 'src/components/empty-content';
// APIs
import { getActivityLogs } from 'src/api/activityLog';
// utils
import { fDate, fDateTime } from 'src/utils/format-time';
// types
import { IActivtyLog } from 'src/types/DTO/activityLog/IActivityLog';
import { ACTIVITY_TYPE, TABLE_NAME } from 'src/types/ApiConstant';
import { useBoolean } from 'src/hooks/use-boolean';
import { DatePicker } from '@mui/x-date-pickers';
import { isValid } from 'date-fns';
import { useLocales } from 'src/locales';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

export default function HistoryTableDataChange({ open, onClose }: Props) {
  const [renderData, setRenderData] = useState<IActivtyLog[]>([]);
  const [page, setPage] = useState(1);
  const [activityLogData, setActivityLogData] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);
  const isReload = useBoolean();
  const pagination = useMemo(() => activityLogData?.pagination, [activityLogData]);
  const { t } = useLocales();

  const [dateParam, setDateParam] = useState<any>();

  const handleFilterDate = useCallback(
    (newValue: Date | null) => {
      if (isValid(newValue)) {
        reset();
        setDateParam(newValue);
        isReload.onToggle();
      }
      if (!newValue) setDateParam(null);
      console.log(newValue);
    },
    [dateParam]
  );

  useEffect(() => {
    if (open) {
      fetch();
    }
    async function fetch() {
      try {
        setIsLoading(true);
        const response = await getActivityLogs({ page, limit: 20, from: fDate(dateParam) });
        if (response.data.list) {
          // console.log(renderData, ' - -- ', response.data.list);
          const newData = [...renderData, ...response.data.list];
          setRenderData(newData);
          setActivityLogData(response.data);
        }
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setIsLoading(false);
      }
    }
  }, [page, isReload.value, open]);

  const handleClick = useCallback(() => {
    setPage((currentPage) => {
      if (!pagination?.totalPages) return currentPage;
      const nextPage = currentPage + 1;
      if (nextPage > pagination.totalPages) return currentPage;
      return nextPage;
    });
  }, [page, pagination]);

  const isLastPage = useCallback(() => {
    return pagination?.totalPages === page;
  }, [page, pagination]);
  const handleClose = useCallback(() => {
    onClose();
    reset(true);
  }, []);
  const reset = useCallback((resetDate?: boolean) => {
    setPage(1);
    setRenderData([]);
    setActivityLogData([]);
    if (resetDate) setDateParam(null);
  }, []);
  return (
    <Drawer open={open} onClose={handleClose} anchor="right">
      <Stack
        spacing={2}
        width={620}
        px={3}
        py={1}
        sx={{ height: '100vh', bgcolor: 'background.default' }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="subtitle2" display="flex" alignItems="center">
              Nhật ký thay đổi dữ liệu :{' '}
              <Tooltip title="Đặt lại mặc định">
                <IconButton
                  onClick={() => {
                    reset(true);
                    isReload.onToggle();
                  }}
                >
                  <Iconify icon="solar:restart-bold" />
                </IconButton>
              </Tooltip>
            </Typography>
            <DatePicker
              label={t('Từ ngày...')}
              value={dateParam}
              onChange={handleFilterDate}
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                },
              }}
              sx={{
                maxWidth: { md: 200 },
              }}
            />
          </Box>

          <IconButton onClick={onClose}>
            <Iconify icon="mdi:close-thick" />
          </IconButton>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />
        {isLoading && renderData.length === 0 && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={80} />
          </Box>
        )}

        {renderData?.length > 0 && (
          <Scrollbar>
            <Stack spacing={1} sx={{ pr: 1 }}>
              {renderData.map((history: IActivtyLog, idx: number) => (
                <HistoryItem key={idx} history={history} />
              ))}
              <LoadingButton
                type="submit"
                color="primary"
                variant="contained"
                loading={isLoading}
                onClick={handleClick}
                disabled={isLastPage()}
              >
                Tải thêm nhật ký
              </LoadingButton>
            </Stack>
          </Scrollbar>
        )}
        {renderData?.length === 0 && !isLoading && (
          <EmptyContent
            filled
            title="Không có hoạt động"
            sx={{
              py: 10,
            }}
          />
        )}
      </Stack>
    </Drawer>
  );
}

// ----------------------------------------------------------------------

const enumColor = {
  [ACTIVITY_TYPE.CREATED]: 'info',
  [ACTIVITY_TYPE.GIVE_BOOK_BACK]: 'success',
  [ACTIVITY_TYPE.UPDATED]: 'warning',
  [ACTIVITY_TYPE.DELETED]: 'error',
} as any;
const enumAction = {
  [ACTIVITY_TYPE.CREATED]: 'TẠO MỚI',
  [ACTIVITY_TYPE.GIVE_BOOK_BACK]: 'TRẢ SÁCH',
  [ACTIVITY_TYPE.UPDATED]: 'CẬP NHẬT',
  [ACTIVITY_TYPE.DELETED]: 'XÓA',
} as any;
function HistoryItem({
  history: { username, action, dataTarget, createdAt, tableTarget },
}: {
  history: IActivtyLog;
}) {
  const targets = JSON.parse(dataTarget);
  return (
    <>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.disabled">
          {fDateTime(createdAt)}
        </Typography>

        <Alert severity={enumColor[action]}>
          Người dùng <strong>{username}</strong> đã thực hiện <strong>{enumAction[action]}</strong>{' '}
          dữ liệu {targets && 'có ID'}{' '}
          <strong> {Array.isArray(targets) ? targets.join(', ') : targets}</strong> vào lúc{' '}
          {fDateTime(createdAt)} tại bảng <strong>{(TABLE_NAME as any)[tableTarget]}</strong>.
        </Alert>
      </Stack>
    </>
  );
}
